import AddIcon from '@mui/icons-material/Add';
import { Box, Chip, FormHelperText, TextField, Tooltip, Typography } from '@mui/material';
import { useRef, useState } from 'react';

interface MuestrasChipsProps {
  numeros: number[];
  onChange: (numeros: number[]) => void;
  allUsedNumbers: Set<number>;
  totalActual: number;
  maxPerPool?: number;
  maxTotal?: number;
  disabled?: boolean;
  showError?: boolean;
}

export function MuestrasChips({
  numeros,
  onChange,
  allUsedNumbers,
  totalActual,
  maxPerPool = 5,
  maxTotal = 15,
  disabled = false,
  showError = false,
}: MuestrasChipsProps): JSX.Element {
  const [adding, setAdding] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const atMaxPerPool = numeros.length >= maxPerPool;
  const atMaxTotal = totalActual >= maxTotal;
  const addDisabled = disabled || atMaxPerPool || atMaxTotal;

  const addTooltip = atMaxPerPool
    ? `Máx. ${maxPerPool} muestras por pool`
    : atMaxTotal
      ? `Máx. ${maxTotal} muestras totales`
      : '';

  function handleDelete(n: number): void {
    onChange(numeros.filter((x) => x !== n));
  }

  function commitInput(): void {
    const raw = inputValue.trim();
    const parsed = parseInt(raw, 10);

    if (isNaN(parsed) || parsed < 1 || String(parsed) !== raw) {
      setInputError('Número inválido (entero ≥ 1)');
      return;
    }
    if (numeros.includes(parsed)) {
      setInputError('Número repetido en este pool');
      return;
    }
    if (allUsedNumbers.has(parsed)) {
      setInputError('Ya está en otro pool');
      return;
    }
    if (numeros.length >= maxPerPool) {
      setInputError(`Máx. ${maxPerPool} muestras por pool`);
      return;
    }
    if (totalActual >= maxTotal) {
      setInputError(`Máx. ${maxTotal} muestras totales`);
      return;
    }

    const next = [...numeros, parsed].sort((a, b) => a - b);
    onChange(next);
    setInputValue('');
    setInputError(null);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitInput();
    } else if (e.key === 'Escape') {
      setAdding(false);
      setInputValue('');
      setInputError(null);
    }
  }

  function handleBlur(): void {
    if (inputValue.trim() === '') {
      setAdding(false);
      setInputError(null);
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {numeros.map((n) => (
          <Chip
            key={n}
            label={n}
            size="small"
            onDelete={disabled ? undefined : () => handleDelete(n)}
          />
        ))}

        {adding ? (
          <Box sx={{ display: 'inline-flex', flexDirection: 'column' }}>
            <TextField
              inputRef={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setInputError(null);
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              autoFocus
              size="small"
              type="number"
              inputProps={{ min: 1 }}
              sx={{ width: 72 }}
              error={!!inputError}
            />
            {inputError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.25 }}>
                {inputError}
              </Typography>
            )}
          </Box>
        ) : (
          <Tooltip title={addDisabled && !disabled ? addTooltip : ''} placement="top">
            <span>
              <Box
                component="button"
                type="button"
                disabled={addDisabled}
                onClick={() => {
                  if (!addDisabled) {
                    setAdding(true);
                    setInputValue('');
                    setInputError(null);
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  border: '1px dashed',
                  borderColor: addDisabled ? 'action.disabled' : 'text.secondary',
                  borderRadius: 1,
                  background: 'none',
                  cursor: addDisabled ? 'default' : 'pointer',
                  color: addDisabled ? 'action.disabled' : 'text.secondary',
                  p: 0,
                  '&:hover:not(:disabled)': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </Box>
            </span>
          </Tooltip>
        )}
      </Box>

      {showError && numeros.length === 0 && (
        <FormHelperText sx={{ color: 'warning.main', mt: 0.5 }}>
          Agregá al menos una muestra.
        </FormHelperText>
      )}
    </Box>
  );
}
